"use client";
import CallingIcon from "@/helpers/svg/callingIcon.svg";
import Chips from "@/helpers/svg/chips.svg";
import ConfirmIcon from "@/helpers/svg/confirmIcon.svg";
import FileConfirmIcon from "@/helpers/svg/fileConfirmIcon.svg";
import InstagramIcon from "@/helpers/svg/instagramIcon.svg";
import PlusIcon from "@/helpers/svg/plusIcon.svg";
import RedRectangle from "@/helpers/svg/redRectangle.svg";
import TelegramIcon from "@/helpers/svg/telegramIcon.svg";
import VerticalSeparator from "@/helpers/svg/verticalSeparator.svg";
import WebpageIcon from "@/helpers/svg/webpageIcon.svg";
import WhatsappContact from "@/helpers/svg/whatsappContact.svg";
import XIcon from "@/helpers/svg/xIcon.svg";
import { ROUTES } from "@/routes/appRoutes";
import { Button, Input } from "@nextui-org/react";
import Cookies from "js-cookie";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { openPaymentGateway, getDiscountCodes } from "@/services/billingService";
import { trackGtagEvent } from "@/services/gtagAnalyticsService";

const Subscription = ({ subscriptions }) => {
  const searchParams = useSearchParams();
  const showSubscriptionMessage = searchParams?.get("msg") === "subscription_required";

  const [purchaseSubsLoading, setPurchaseSubsLoading] = useState(false);
  const [discountPolicy, setDiscountPolicy] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [codeValidating, setCodeValidating] = useState(false);
  const [discountErr, setDiscountErr] = useState("");

  const subscriptionItems = subscriptions?.data;

  useEffect(() => {
    trackGtagEvent("subscription_screen_opened", {
      source: showSubscriptionMessage ? "subscription_required" : "direct",
      plans_count: subscriptionItems?.length || 0,
    });
  }, [showSubscriptionMessage, subscriptionItems?.length]);

  const subsLifeTime = (days) => {
    if (days % 30 === 0) {
      const months = days / 30;
      return `اشتراک ${months} ماهه`;
    } else {
      return `اشتراک ${days} روزه`;
    }
  };

  async function handleBuySubscription(subsName) {
    const token = Cookies.get("userAccessToken");
    const selectedSubscription = subscriptionItems?.find((item) => item.name === subsName);

    trackGtagEvent("purchase_started", {
      order_type: "subscription",
      subscription_key: subsName,
      value: getPriceForItem(selectedSubscription),
      currency: "IRR",
      has_discount: Boolean(discountPolicy),
    });

    setPurchaseSubsLoading(true);
    try {
      const response = await openPaymentGateway(
        {
          subscriptionKey: subsName,
          discountCode: discountPolicy ? discountPolicy.code : undefined,
          device: "web",
        },
        token
      );

      if (response.status === 200) {
        enqueueSnackbar("در حال انتقال به درگاه پرداخت...", {
          variant: "info",
        });
        const { data } = response.data;
        window.location.href = data;
      } else {
        trackGtagEvent("purchase_failed", {
          order_type: "subscription",
          subscription_key: subsName,
          failure_stage: "gateway_create",
          status_code: response?.status,
        });
        enqueueSnackbar(
          response?.data?.message || "خطای نامشخص در خرید اشتراک",
          {
            variant: "error",
          }
        );
      }
    } catch (err) {
      trackGtagEvent("purchase_failed", {
        order_type: "subscription",
        subscription_key: subsName,
        failure_stage: "gateway_create",
        status_code: err.response?.status,
      });
      if (err.response?.status === 400) {
        enqueueSnackbar("شما در حال حاضر اشتراک فعال دارید.", {
          variant: "error",
        });
      } else {
        enqueueSnackbar(
          err.response?.data?.message ||
          err.message ||
          "خطای نامشخص در خرید اشتراک",
          {
            variant: "error",
          }
        );
      }
      return err;
    } finally {
      setPurchaseSubsLoading(false);
    }
  }

  const validateDiscountCodeForPurchase = (item) => {
    const { usedCount, useCount, isLocked } = item;
    if (usedCount >= useCount) {
      return setDiscountErr("تعداد دفعات استفاده این کد تخفیف به پایان رسیده");
    } else if (isLocked === 1) {
      return setDiscountErr(
        "استفاده همزمان از یک کد تخفیف در چند خرید ممکن نمی باشد، لطفا بعد از چند دقیقه دوباره تلاش کنید"
      );
    }
    let hasCorrespondantItem = false;

    subscriptionItems?.map((sub) => {
      if (sub.price >= item?.minPrice || 0) {
        hasCorrespondantItem = true;
      }
    });

    if (!hasCorrespondantItem) {
      return setDiscountErr(
        `حداقل مبلغ برای استفاده از این کد تخفیف ${item.minPrice.toLocaleString()} تومان می باشد`
      );
    } else {
      setDiscountPolicy(item);
      enqueueSnackbar("کد تخفیف با موفقیت اعمال شد", {
        variant: "success",
      });
    }
  };

  const getDiscountPolicy = async () => {
    if (discountCode?.length < 4) {
      return setDiscountErr("کد تخفیف باید حداقل ۴ حرفی باشد");
    } else {
      setDiscountErr(false);
    }

    setCodeValidating(true);

    try {
      const response = await getDiscountCodes(
        {
          pageNumber: 1,
          pageIndex: 10,
          discountCode: discountCode,
        },
        Cookies.get("userAccessToken")
      );

      if (response.status === 200) {
        const { data } = response.data;

        if (data.results?.length > 0) {
          const item = data.results[0];
          return validateDiscountCodeForPurchase(item);
        } else {
          setDiscountErr("کد تخفیف معتبر نمی باشد");
        }
      } else {
        enqueueSnackbar(
          response?.data?.message || "خطای نامشخص در اعتبارسنجی کد تخفیف ",
          {
            variant: "error",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message ||
        err.message ||
        "خطای نامشخص در خرید اشتراک",
        {
          variant: "error",
        }
      );
    } finally {
      setCodeValidating(false);
    }
  };

  const getPriceForItem = (item) => {
    if (!item) return undefined;
    const { price } = item;
    if (!price) return;
    if (
      !discountPolicy ||
      price < discountPolicy.minPrice ||
      discountPolicy.usedCount >= discountPolicy.useCount
    )
      return item.price;

    switch (discountPolicy.type) {
      case "percent":
        return ((100 - discountPolicy.discountValue) / 100) * item.price;
      case "amount":
        return item.price - discountPolicy.discountValue;
      default:
        return item.price;
    }
  };

  return (
    <div className="min-h-[100vh] h-auto px-6 py-12 md:p-12">
      <div className="flex flex-col justify-center items-start md:container mx-auto mt-5 md:mt-0">
        {showSubscriptionMessage && (
          <div className="w-full max-w-2xl mx-auto mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 px-5 py-4 text-center">
            <p className="text-amber-400 font-bold text-lg mb-1">اشتراک فعال ندارید</p>
            <p className="text-amber-300/80 text-sm">برای تماشای این محتوا نیاز به خرید اشتراک دارید. یکی از طرح‌های زیر را انتخاب کنید.</p>
          </div>
        )}
        <h2 className="text-primary-white text-xl font-bold text-center w-full pt-4 md:pb-6 lg:mt-16">
          اشتراک خود را انتخاب نمایید:
        </h2>
        <div className="hidden lg:flex justify-center gap-5 items-center w-full pt-8">
          {subscriptionItems?.map((subscription) => {
            return (
              <div
                key={subscription._id}
                className="h-auto subs-shadow relative"
              >
                <div className="flex flex-col subs-top-bg justify-center items-center relative">
                  <div className="flex flex-col justify-around items-center text-primary-white text-lg">
                    <h3 className="pb-3 text-2xl font-black">
                      {subscription.name}
                    </h3>
                    <span>{subsLifeTime(subscription.lifeTimeDays)}</span>
                    {discountPolicy && (
                      <p className="m-0 text-zinc-500 relative overflow-visible px-3">
                        {subscription.price} تومان
                        <div className="absolute top-0 left-[-10px] bottom-0 right-[-10px] flex justify-center items-center overflow-visible">
                          <hr className="w-full border-zinc-400 rotate-10" />
                        </div>
                      </p>
                    )}
                    <Button
                      disableRipple
                      className="rounded-full mt-4 font-semibold subs-button text-primary-white cursor-pointer z-20"
                      fullWidth
                      isDisabled={purchaseSubsLoading}
                      onClick={() => handleBuySubscription(subscription.name)}
                    >
                      {getPriceForItem(subscription) === 0 ? (
                        "رایگان!"
                      ) : (
                        <>
                          <span className="text-xl font-black">
                            {getPriceForItem(subscription)?.toLocaleString()}
                          </span>
                          تومان
                        </>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="flex flex-col subs-bottom-bg pb-[242px] text-primary-white justify-start items-center relative">
                  <div className="flex flex-col justify-start items-center my-3 max-w-[200px]">
                    <div className="flex justify-center items-center w-full">
                      <span className="w-full mb-4 font-medium text-center">
                        {subscription.description.title}
                      </span>
                    </div>
                    {subscription.description.subTitles?.map((title, index) => (
                      <div
                        key={index}
                        className="flex justify-center items-center w-full"
                      >
                        <RedRectangle />
                        <span className="mr-2 text-center">{title}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2">
                    <PlusIcon />
                  </div>
                  <div className="flex justify-center items-center mt-3">
                    <Chips />
                    <span className="mr-2 chips-color font-bold text-lg">
                      {subscription.rewards[0].amount} ژتون بگیر
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex flex-col justify-start items-center lg:hidden h-auto min-h-52 sm:min-h-52 lg:min-h-[45vh] gap-5 w-full pt-12">
          {subscriptionItems?.map((subscription) => {
            return (
              <div
                key={subscription._id}
                className="flex flex-col justify-between items-center w-full"
              >
                <div className="flex flex-col justify-between items-center rounded-xl bg-[#262626] text-primary-white w-full text-lg px-3 sm:px-6 py-4">
                  <div className="flex justify-between items-center w-full pb-4">
                    <div className="flex flex-row justify-between items-center w-full">
                      <div className="flex flex-row gap-1 justify-between items-center">
                        <h3 className="pb-1 xs:text-xs sm:text-sm md:text-xl font-black">
                          {subscription.name}
                        </h3>
                        <div className="block mr-2 sm:mr-3">
                          <VerticalSeparator />
                        </div>
                        <span className="mr-2 sm:mr-3 xs:text-xs sm:text-sm md:text-xl">
                          {subsLifeTime(subscription.lifeTimeDays)}
                        </span>
                      </div>
                      {discountPolicy && (
                        <p className="m-0 text-zinc-500 relative overflow-visible px-3">
                          {subscription.price} تومان
                          <div className="absolute top-0 left-[-10px] bottom-0 right-[-10px] flex justify-center items-center overflow-visible">
                            <hr className="w-full border-zinc-400 rotate-10" />
                          </div>
                        </p>
                      )}
                      <Button
                        className="w-fit rounded-full px-2 font-semibold subs-button text-primary-white cursor-pointer z-20"
                        fullWidth
                        onClick={() => handleBuySubscription(subscription.name)}
                      >
                        {getPriceForItem(subscription) === 0 ? (
                          "رایگان!"
                        ) : (
                          <>
                            <span className="text-xl font-black">
                              {getPriceForItem(subscription)?.toLocaleString()}
                            </span>
                            تومان
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-row justify-between items-center w-full">
                    <span className="text-[10px] sm:text-sm md:text-base text-right mb-4">
                      تماشای نامحدود فیلم، سریال و برنامه‌اختصاصی
                    </span>
                    <div className="flex gap-2 items-center w-44 sm:w-40">
                      <PlusIcon />
                      <div className="flex justify-center items-center">
                        <Chips />
                        <span className="mr-2 chips-color font-bold text-[10px] sm:text-sm">
                          {subscription.rewards[0].amount} ژتون بگیر
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="bg-zinc-900 rounded-xl border-zinc-800 p-5 w-full mx-auto max-w-2xl mt-10">
          <p className="font-bold">کد تخفیف دارید؟</p>
          <p className="font-light">
            کد تخفیف را وارد کرده و بر روی دکمه اعمال برنید
          </p>
          <div className="flex gap-3 w-full mt-5">
            <Input
              className="flex-auto"
              placeholder="کد تخفیف"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
              disabled={codeValidating || discountPolicy}
            />
            <Button
              disableRipple
              isLoading={codeValidating}
              onClick={() => {
                if (discountPolicy) {
                  setDiscountPolicy("");
                  setDiscountCode("");
                } else {
                  getDiscountPolicy();
                }
              }}
            >
              {discountPolicy ? "حذف" : "اعمال"}
            </Button>
          </div>
          {Boolean(discountErr) && (
            <p className="text-red-500">{discountErr}</p>
          )}
        </div>
        <div className="flex flex-col items-start justify-center w-full pt-12 lg:pt-24 lg:w-[70%] lg:mx-auto">
          <div className="flex flex-row text-primary-white justify-start md:px-12 xl:px-24 w-full items-center mb-8">
            <div>
              <ConfirmIcon />
            </div>
            <p className="mr-2 md:text-lg lg:text-xl">
              تماشای نامحدود هزاران فیلم و سریال و انیمیشن جذاب منه باخ در طول
              مدت اشتراک خریداری شده
            </p>
          </div>
          <div className="flex flex-row text-primary-white justify-start md:px-12 xl:px-24 w-full items-center mb-8">
            <div>
              <ConfirmIcon />
            </div>
            <p className="mr-2 md:text-lg lg:text-xl">
              همزمان به زبان های ترکی، فارسی و خارجی فیلم وسریال ببین
            </p>
          </div>
          <div className="flex flex-row text-primary-white justify-start md:px-12 xl:px-24 w-full items-center mb-8">
            <div>
              <ConfirmIcon />
            </div>
            <p className="mr-2 md:text-lg lg:text-xl">
              امکان دانلود درون برنامه ای محتواهای منه باخ و تماشای آن در زمانی
              که دسترسی به اینترنت ندارید
            </p>
          </div>
          <div className="flex flex-row text-primary-white justify-start md:px-12 xl:px-24 w-full items-center mb-8">
            <div>
              <ConfirmIcon />
            </div>
            <p className="mr-2 md:text-lg lg:text-xl">
              با یک اشتراک به صورت گروهی منه باخ را تماشا کنید
            </p>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row h-[40vh] justify-around items-center relative mt-4 w-full lg:w-[70%] lg:mx-auto lg:mt-0 mb-12">
          <div className="flex flex-col justify-center items-center text-primary-white w-full lg:w-[350px] text-xl cursor-pointer">
            <span className="text-base">صندوق پستی</span>
            <div className="flex flex-row w-full justify-center items-center gr-border bg-[#000000] mt-4">
              <span className="ml-2">ad@manabakh.ir</span>
              <WebpageIcon />
            </div>
          </div>
          <div className="flex flex-col justify-center items-center text-primary-white w-full lg:w-[350px] text-xl cursor-pointer">
            <span className="text-base">تماس</span>
            <div className="flex flex-row w-full justify-center items-center gr-border bg-[#000000] mt-4">
              <span className="ml-2">۰۲۱-۲۶۲۱۲۶۸۲</span>
              <CallingIcon />
            </div>
          </div>
          <div className="flex flex-col justify-center items-center text-primary-white w-full lg:w-[350px] text-xl cursor-pointer">
            <span className="text-base">شبکه‌های اجتماعی</span>
            <div className="flex flex-row w-full justify-center items-center gr-border bg-[#000000] mt-4">
              <div className="flex justify-center items-center my-2">
                <a className="cursor-pointer ml-2">
                  <TelegramIcon />
                </a>
                <a className="cursor-pointer ml-2">
                  <WhatsappContact />
                </a>
                <a className="cursor-pointer ml-2">
                  <XIcon />
                </a>
                <a className="cursor-pointer ml-2">
                  <InstagramIcon />
                </a>
                <a className="cursor-pointer ml-2">
                  <FileConfirmIcon />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
